import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api, type Feed, type MarketSummary } from "./api";
import {
  COMMAND_NAMES,
  isClear,
  type Line,
  optionsForCommand,
  type Prompt,
  runCommand,
  type Session,
} from "./commands";
import { complete } from "./complete";
import { browserStorage, historyKey, loadHistory, pushHistory, saveHistory } from "./history";
import { markPortfolio } from "./marking";
import { checklist, currentStep, type OnboardingState, say } from "./onboarding";
import { DEFAULT_SETUP, resolveSetup, resolveTheme, SETUPS, THEMES } from "./setups/registry";
import {
  type AccountEvent,
  type AccountSnapshot,
  type FeedChange,
  Stream,
  type Tick,
} from "./stream";
import {
  changePercent,
  isOpen,
  keepOnly,
  mergeTicks,
  replaceTicks,
  type TickerQuote,
} from "./ticker";

/**
 * The terminal, without its chrome.
 *
 * **This is the seam M3/I2 exists to cut.** Everything that talks to the server
 * or holds a number lives here; a setup receives what this returns and draws it.
 * The alternative — a second look added the obvious way — is a second copy of
 * this state, and then every command added in M3 onwards has to be wired into
 * both of them.
 *
 * It is also PRODUCT.md's rule applied one level down. The terminal must never
 * compute something the API does not expose; a *setup* must never compute
 * anything at all. The only import of `api.ts` in the terminal is the one in
 * this file, and `test/setup-boundary.test.ts` fails the build if a setup grows
 * another.
 *
 * **M2/I4 replaced the 5s poll with a live socket.** Prices arrive unasked, and
 * so do account events — an order that fills by itself now says so on screen
 * instead of waiting for the user to type `orders` again (M2/E6).
 *
 * The two numbers still share a clock, which is what M2/E9 was about: every
 * tick re-marks the portfolio from the *same* prices the ticker is showing, so
 * the header and the row beneath it can never drift apart. Between account
 * snapshots the equity figure is computed here — with the server's own
 * `notional()`, see `marking.ts`.
 */

/**
 * What the terminal says before anybody has typed anything.
 *
 * **It names the two commands that are otherwise unfindable**, and it is the
 * whole of the guided first run now. The tour used to speak on its own, one
 * step at a time, as the derived state advanced; that was removed because
 * unprompted output in a terminal is the shell talking over the reader.
 *
 * `feed` earns its line rather than being left to `help`. Bringing your own
 * market-data licence is the arrangement this platform is built on — a trader
 * who never runs it is priced by the simulator forever and has no reason to
 * suspect otherwise, because a simulated price looks exactly like a real one.
 */
const BANNER = [
  "agentic trading cloud — paper terminal",
  "no real money moves here. `help` lists every command.",
  "`help` shows the next step of your first trade. `feed` connects your own market data.",
];

/** What a terminal watches before anyone has said otherwise. */
const TICKER_SYMBOLS = ["AAPL", "NVDA", "SPY", "MSFT", "TSLA"];

const SETUP_KEY = "atc.setup";
const THEME_KEY = "atc.theme";

export type { Direction } from "./ticker";

/**
 * A ticker row as a setup receives it.
 *
 * `direction` is the last real price move, held across frames that repeat a
 * price; `open` is whether the venue was trading when the price was taken. Two
 * facts, because they answer two different questions and colouring one glyph
 * with both is how M4/E11 happened. Folded in `ticker.ts`, which is pure.
 */
export type Quote = TickerQuote & {
  open: boolean;
  /** How far the day has moved, in percent, or undefined with no reference. */
  changePercent: number | undefined;
};

export type LinkState = "connecting" | "live" | "offline";

/**
 * How long the header will hold a market's state before asking again.
 *
 * The response names the instant it next changes, so the refresh is scheduled
 * for that instant rather than polled for — one request per session per
 * transition, not one per minute per browser, which matters at the scale
 * ARCH-overview sizes for. The cap is a floor under wrongness: a browser whose
 * clock is off, or a calendar that named an instant far away, re-asks within a
 * quarter of an hour instead of trusting arithmetic it did badly.
 */
const MARKETS_MAX_WAIT_MS = 15 * 60 * 1000;

/** Long enough after a transition that the server has certainly crossed it too. */
const MARKETS_SETTLE_MS = 3000;

export type Marked = { equity: string; marketValue: string; marked: number };

/**
 * What a setup is given, and the whole of it.
 *
 * Deliberately a plain value plus callbacks: a setup cannot reach past this to
 * fetch anything, and it does not need to, because everything on screen in
 * either look is already here.
 */
/**
 * The three fields of the selected grant a header can use.
 *
 * A `Pick` rather than a shape of its own, so it cannot drift from what
 * `GET /api/v1/feeds` actually publishes. It deliberately carries no `id`,
 * `key_id` or `verified_at`: a status line names what is pricing you, and the
 * key behind it belongs to `feed`, which can explain itself at length.
 */
export type FeedBadge = Pick<Feed, "provider" | "kind" | "because">;

export type TerminalViewModel = {
  lines: readonly Line[];
  input: string;
  busy: boolean;
  session: Session;
  quotes: readonly Quote[];
  /** The server's last authoritative word, or undefined when signed out. */
  snapshot: AccountSnapshot | undefined;
  /** That snapshot marked to the latest ticks. Undefined when there is no snapshot. */
  marked: Marked | undefined;
  link: LinkState;
  /** What the terminal is waiting to be told, if anything. Setups show its label. */
  pending: Prompt | undefined;
  /** The venues this deployment serves, exactly as `/api/v1/markets` describes them. */
  markets: readonly MarketSummary[];
  /**
   * What is pricing this trader, and why. Undefined until it has been read.
   *
   * **The banner needs it because a venue is not an answer** (`M4/E53`). The
   * header said `US` when a trader's own key was working and `SIM` when it had
   * stopped, and left the mapping to be inferred — so a trader on the simulator
   * and a trader on their own Alpaca licence read an identical line. `M4/E52`
   * made the venue react to a lapse; this is what makes the reaction legible.
   */
  feed: FeedBadge | undefined;
  /**
   * The LIVE Alpaca account the selected account trades through, or undefined
   * for a paper connection, none, or no answer (`ADR-0059`, `M8/E13`). Real
   * money is the one fact a header must never leave to be inferred.
   */
  liveVenue: { accountNumber: string | null } | undefined;
  setupId: string;
  themeId: string;
  setInput: (value: string) => void;
  submit: (raw: string) => void;
  onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  /** The scrollback pane, which the shell scrolls to the bottom after a command. */
  scrollRef: React.RefObject<HTMLDivElement | null>;
  /** The prompt. A terminal that needs a mouse click to accept a command is not one. */
  inputRef: React.RefObject<HTMLInputElement | null>;
  /** The outermost element: clicking anywhere in it focuses the prompt. */
  shellRef: React.RefObject<HTMLDivElement | null>;
};

/** The stored preference, or the `?setup=` override a screenshot run passes. */
function initialChoice(key: string, param: string, valid: (id: string) => boolean): string {
  if (typeof window === "undefined") return "";
  const fromUrl = new URLSearchParams(window.location.search).get(param);
  if (fromUrl && valid(fromUrl)) return fromUrl;
  try {
    const stored = window.localStorage.getItem(key);
    if (stored && valid(stored)) return stored;
  } catch {
    // Safari in private mode throws on localStorage. A terminal that will not
    // start because it could not remember a colour scheme is a worse bug than
    // forgetting the colour scheme.
  }
  return "";
}

const knownSetup = (id: string) => SETUPS.some((s) => s.id === id);
const knownTheme = (id: string) => THEMES.some((t) => t.id === id);

function remember(key: string, value: string) {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // As above: the preference is a convenience, not a requirement.
  }
}

/**
 * What to print when a trader's feed changes without them asking.
 *
 * **Said in the scrollback, not only in the header.** A number quietly becoming
 * a different number is exactly the change a reader does not notice; a line
 * arriving in the flow is the one they do. This is the same argument
 * `describeEvent` makes for fills.
 *
 * `lapsed` is the case that matters and the one that brought this about: a key
 * revoked at the broker, or a plan that stopped including a feed. It is
 * deliberately an `err` line — the trader did not choose this and their prices
 * have changed underneath them — while a change they *did* choose is a plain
 * note, because telling somebody in red about a thing they just did is noise.
 */
export function feedChangeLines(change: FeedChange): Line[] {
  if (!change.lapsed) {
    return [{ kind: "note", text: `you are now priced by ${change.selected}.` }];
  }

  return [
    {
      kind: "err",
      text: `your ${change.lapsed} feed stopped working — you are on ${change.selected}.`,
    },
    {
      kind: "note",
      text: `  \`feed\` to see why, and to reconnect.`,
    },
  ];
}

export function useTerminal(): TerminalViewModel {
  const [lines, setLines] = useState<Line[]>(BANNER.map((text) => ({ kind: "note", text })));
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [session, setSessionState] = useState<Session>({});
  const [rawQuotes, setQuotes] = useState<TickerQuote[]>([]);

  /**
   * What the ticker is watching — one list, whoever is asking.
   *
   * It used to live in three places that could disagree: the socket's private
   * set, the hardcoded array the guest poll asked for, and whatever quotes
   * happened to be in state. So `unwatch` stopped the feed and left the price on
   * screen forever, and signed OUT it did nothing at all — a guest has no
   * socket, and both `watch` and `unwatch` reported success having changed
   * nothing (M4/E15).
   *
   * State rather than a ref because the guest poll's effect depends on it: a ref
   * would change without re-subscribing, and the poll would go on asking for
   * the symbols it closed over.
   */
  const [watched, setWatched] = useState<readonly string[]>(TICKER_SYMBOLS);

  /**
   * The same list, readable from a callback that must not re-run when it
   * changes — the socket effect is keyed on the signed-in user, and depending on
   * the watch list there would tear the connection down and rebuild it every
   * time somebody watched a symbol.
   */
  const watchedRef = useRef<readonly string[]>(watched);
  /** What Tab offers for a feed; refreshed with the feed badge. */
  const feedNamesRef = useRef<readonly string[]>([]);

  /**
   * The one place the watch list changes, so the subscription, the poll and the
   * rows on screen cannot disagree. Pruning the quotes is what M4/E15 was
   * about: the feed stopping is not the same as the price leaving the screen.
   */
  const applyWatched = useCallback((next: readonly string[]) => {
    watchedRef.current = next;
    setWatched(next);
    setQuotes((previous) => keepOnly(previous, next));
  }, []);

  /**
   * Symbols watched only because the selected account holds them (`M8/E25`),
   * so they can be let go when it stops holding them — unless the trader
   * watched one themselves, which takes it out of this set.
   */
  const heldWatchRef = useRef<Set<string>>(new Set());

  const watchSymbols = useCallback(
    (symbols: string[]) => {
      const wanted = symbols.map((x) => x.trim().toUpperCase()).filter((x) => x.length > 0);
      for (const w of wanted) heldWatchRef.current.delete(w);
      const next = [...new Set([...watchedRef.current, ...wanted])];
      applyWatched(next);
      streamRef.current?.watch(wanted);
    },
    [applyWatched],
  );

  const unwatchSymbols = useCallback(
    (symbols: string[]) => {
      const gone = new Set(symbols.map((x) => x.trim().toUpperCase()));
      applyWatched(watchedRef.current.filter((x) => !gone.has(x.toUpperCase())));
      streamRef.current?.unwatch([...gone]);
    },
    [applyWatched],
  );
  const [history, setHistory] = useState<string[]>([]);
  const [historyAt, setHistoryAt] = useState(-1);
  /**
   * Which stored list is this user's (`M9/E7`). Every change is written through
   * at once, so a reload, a crash or a closed tab loses nothing typed.
   */
  const historyRef = useRef<{ key: string; list: string[] }>({
    key: historyKey(undefined),
    list: [],
  });
  const writeHistory = useCallback((next: string[]) => {
    historyRef.current = { ...historyRef.current, list: next };
    saveHistory(browserStorage(), historyRef.current.key, next);
    setHistory(next);
    setHistoryAt(-1);
  }, []);
  // Signing in, out, or as someone else switches to that user's own list.
  useEffect(() => {
    const key = historyKey(session.userId);
    const list = loadHistory(browserStorage(), key);
    historyRef.current = { key, list };
    setHistory(list);
    setHistoryAt(-1);
  }, [session.userId]);
  const [link, setLink] = useState<LinkState>("offline");

  /**
   * The question a command asked, waiting for its answer.
   *
   * The shell owns exactly two things about it: the next line goes to
   * `onAnswer` instead of to the grammar, and that line does not enter history.
   * Everything about WHAT is being asked stays in the command — see `Prompt`.
   */
  const [pending, setPending] = useState<Prompt | undefined>(undefined);

  /**
   * The venues this deployment serves, and what they are doing.
   *
   * Fetched rather than derived: whether a market is open, and when it next
   * changes, is the server's answer — the terminal holding a calendar is the
   * exact failure PRODUCT.md's rule exists to prevent, and a UI calendar looks
   * right until the day it disagrees with the engine that fills the orders.
   */
  const [markets, setMarkets] = useState<readonly MarketSummary[]>([]);
  /**
   * Re-read the market list out of band.
   *
   * The scheduled reload waits for the next open/close transition. That is the
   * right schedule for a venue changing state and the wrong one for **the set
   * of venues changing underneath the reader**, which is what a revoked key
   * does: the trader lapses to the simulator, `/api/v1/markets` starts
   * answering `SIM`, and the header goes on showing `US` for up to a quarter of
   * an hour.
   */
  const reloadMarketsRef = useRef<(() => void) | undefined>(undefined);

  /**
   * Whether the user has asked for a price of their own.
   *
   * Set from the command that was typed rather than from the watch list, which
   * starts non-empty: `watch NVDA` on a symbol already in the default five
   * changes no observable state, and a tour step nobody can complete is worse
   * than no step at all.
   */
  const [priced, setPriced] = useState(false);

  /**
   * Whether an order has ever been placed in this session — the shell sees fills.
   *
   * State rather than a ref, and that is not a style choice: it is read inside
   * the memo that decides where the tour is, and a ref cannot make a memo
   * recompute. As a ref the first order placed did not advance the tour until
   * something unrelated happened to change the snapshot. Same mistake `priced`
   * above was fixed for in M4/I8.
   */
  const [ordered, setOrdered] = useState(false);

  /**
   * Which chrome is worn, and in what colours.
   *
   * An empty string means "not chosen" — the setup's own default theme applies,
   * so a setup can ship a look without every user having to pick one, and
   * picking one still overrides it for every setup.
   */
  const [setupId, setSetupId] = useState(() => initialChoice(SETUP_KEY, "setup", knownSetup));
  const [themeChoice, setThemeChoice] = useState(() =>
    initialChoice(THEME_KEY, "theme", knownTheme),
  );

  const setup = resolveSetup(setupId);
  const themeId = resolveTheme(themeChoice || setup.defaultTheme).id;

  /**
   * The server's last authoritative word on this account, and the marks since.
   *
   * Kept apart on purpose. The snapshot is what the API said; `marks` is what
   * has ticked since. Equity is derived from the pair, so a stale mark can
   * never quietly become the account's official position — the next snapshot
   * overwrites the base and the marks re-apply on top of it.
   */
  const [snapshot, setSnapshot] = useState<AccountSnapshot | undefined>();

  /**
   * Whether the trader has chosen a feed, from the server rather than from a
   * flag this file sets when `feed` runs.
   *
   * The distinction matters for the same reason every other onboarding
   * predicate is derived: somebody who chose on a previous visit, in another
   * tab, or through the API directly has done the step, and a tour that only
   * counted the command typed *here* would ask them to do it again. `chosen`
   * exists on the API for this.
   */
  const [feedChosen, setFeedChosen] = useState(false);

  /**
   * The selected grant, for the header — read from the same response as
   * `feedChosen` and for the same reason: the server's answer, not this file's
   * memory of what was typed here.
   */
  const [feed, setFeed] = useState<FeedBadge | undefined>();
  const [liveVenue, setLiveVenue] = useState<{ accountNumber: string | null } | undefined>();
  const [marks, setMarks] = useState<ReadonlyMap<string, string>>(new Map());

  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const shellRef = useRef<HTMLDivElement>(null);

  /**
   * Clicking anywhere focuses the prompt, the way a terminal emulator behaves.
   *
   * Attached as a DOM listener rather than a JSX `onClick`, because this is not
   * a semantic interaction — the div is not a control, it has no keyboard
   * equivalent to offer, and giving it a role would misdescribe it to a screen
   * reader. Selecting text still works: only a collapsed selection refocuses.
   *
   * It lives on the shell root rather than inside a setup, so every setup gets
   * it and no setup can forget it.
   */
  useEffect(() => {
    const shell = shellRef.current;
    if (!shell) return;

    const onPointerUp = () => {
      if ((window.getSelection()?.toString().length ?? 0) > 0) return;
      inputRef.current?.focus();
    };

    shell.addEventListener("pointerup", onPointerUp);
    return () => shell.removeEventListener("pointerup", onPointerUp);
  }, []);

  /**
   * Which account everything below is about.
   *
   * A ref rather than state because `refresh` runs outside React's render and
   * has to read the current answer at the moment it is called, not the one that
   * was current when its closure was built.
   *
   * **It is the same fact as `session.accountId`, so `setSession` writes it**
   * — see there for what happened when it did not.
   */
  const accountIdRef = useRef<string | undefined>(undefined);
  const accountUserIdRef = useRef<string | undefined>(undefined);

  const setSession = useCallback((patch: Partial<Session>) => {
    /*
     * `M7/E5`: the chosen account moves here, synchronously, or the banner
     * describes a different account from every command under it.
     *
     * `account` does `ctx.setSession({ accountId })` and then `ctx.refresh()`
     * on the very next line, so anything deferred — an effect, a state update
     * read on the next render — is too late by one command: `refresh` would
     * still see the previous id. Writing the ref inside the patch is what makes
     * the two agree at the only moment that matters.
     *
     * Found on production, where `account US primary` selected the US account,
     * every command answered for it, and the banner went on reporting SIM's
     * money — then re-selecting the same account put SIM back, because the
     * switch itself was calling a refresh that re-read `accounts[0]`.
     *
     * `in` rather than a truthiness test on purpose: `logout` patches
     * `accountId: undefined` and means it.
     */
    if ("accountId" in patch) accountIdRef.current = patch.accountId;
    setSessionState((prev) => ({ ...prev, ...patch }));
  }, []);

  /**
   * Pull the account's authoritative numbers.
   *
   * Still exists, and is no longer a poll. It runs on sign-in, on reconnect,
   * and after any command that changes something — the socket covers everything
   * in between. `/positions` rather than `/accounts/:id` because it returns the
   * per-symbol holdings the browser needs to mark, and it is the same shape
   * `AccountDO.snapshot` pushes down the socket, so both paths land in one
   * piece of state instead of two that can disagree.
   *
   * Signed out, it does nothing at all. Asking as a guest is a 401 for no
   * information.
   */

  const refresh = useCallback(async () => {
    let id = accountIdRef.current;
    if (!id) {
      const list = await api.listAccounts();
      if (!list.ok || !list.data.accounts[0]) {
        setSnapshot(undefined);
        return;
      }
      id =
        list.data.selected_account_id ??
        (!("selected_account_id" in list.data) || list.data.accounts.length === 1
          ? list.data.accounts[0]?.id
          : undefined);
      if (!id) {
        setSnapshot(undefined);
        return;
      }
      accountIdRef.current = id;
      const selected = list.data.accounts.find((account) => account.id === id);
      if (selected)
        setSessionState((prev) => ({ ...prev, accountId: id, accountName: selected.name }));
    }

    const [positions, venue] = await Promise.all([api.positions(id), api.mirror(id)]);
    if (positions.ok) setSnapshot(positions.data as AccountSnapshot);
    // The server's word on which Alpaca account this one trades through; no
    // answer shows nothing rather than a guess.
    const m = venue.ok ? venue.data.mirror : null;
    setLiveVenue(m?.trading_account === "live" ? { accountNumber: m.account_number } : undefined);
  }, []);

  /**
   * Read the feed selection when the session changes, and after `feed` runs.
   *
   * Separate from `refresh`, which is about the account: this is behind a
   * session but not behind an account, and folding it in would mean a trader
   * who has not opened an account yet — which is the step *after* this one —
   * never has their feed choice read at all.
   */
  const refreshFeed = useCallback(async () => {
    const feeds = await api.feeds();
    feedNamesRef.current = feeds.ok ? feeds.data.feeds.map((f) => f.provider) : [];
    setFeedChosen(feeds.ok ? feeds.data.chosen : false);
    // The grant the server says is selected, rather than the one this file
    // last saw chosen — `select` can be refused, and a lapse changes it with
    // nobody typing anything.
    setFeed(feeds.ok ? feeds.data.feeds.find((f) => f.selected) : undefined);
  }, []);

  /**
   * Forget the remembered account whenever the signed-in user changes.
   *
   * Without this, signing out and back in as someone else leaves the previous
   * user's account id in the ref. The read that follows is refused by RLS — so
   * `setAccount` is never called, and the header keeps displaying the previous
   * user's equity and cash to the new one. The server never leaks anything; the
   * screen does.
   */
  useEffect(() => {
    const previousUserId = accountUserIdRef.current;
    accountUserIdRef.current = session.userId;
    if (!session.userId) {
      accountIdRef.current = undefined;
      setSnapshot(undefined);
      setMarks(new Map());
      setFeedChosen(false);
      setFeed(undefined);
      return;
    }
    if (previousUserId && previousUserId !== session.userId) {
      accountIdRef.current = undefined;
      setSnapshot(undefined);
      setMarks(new Map());
      setFeedChosen(false);
      setFeed(undefined);
    }
    void refreshFeed();
  }, [session.userId, refreshFeed]);

  // Restore a session on load: the cookie may still be valid from last visit.
  useEffect(() => {
    void (async () => {
      const who = await api.whoami();
      if (who.ok) {
        setSession({
          email: who.data.email,
          userId: who.data.user_id,
          // Restored from the server rather than assumed. A cookie from last
          // visit may predate the current terms, and a terminal that assumed
          // otherwise would let the user type into an account the API refuses.
          agreementRequired: who.data.agreement_required,
        });
        setLines((l) => [...l, { kind: "note", text: `session restored — ${who.data.email}` }]);
        if (who.data.agreement_required) {
          setLines((l) => [
            ...l,
            { kind: "note", text: "the terms of use have not been accepted — run `terms`" },
          ]);
        }
        await refresh();
      }
    })();
  }, [refresh, setSession]);

  /**
   * Ask what the markets are doing, then again when one of them says it changes.
   *
   * Public and unauthenticated, so this runs for a guest too — the landing
   * screen saying whether the market is open is most of what tells a visitor
   * this is a real venue rather than a demo.
   */
  useEffect(() => {
    let alive = true;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const load = async () => {
      const res = await api.markets();
      if (!alive) return;
      if (!res.ok) {
        // Try again at the cap rather than giving up: a header that goes blank
        // on one failed request and never recovers is worse than a stale one.
        timer = setTimeout(load, MARKETS_MAX_WAIT_MS);
        return;
      }

      setMarkets(res.data.markets);

      // The soonest transition any venue named. Computed from instants the
      // server chose — the terminal is deciding when to ask again, not what the
      // answer is.
      const next = res.data.markets
        .map((m) => (m.state === "open" ? m.closes_at : m.opens_at))
        .filter((iso): iso is string => typeof iso === "string")
        .map((iso) => Date.parse(iso) - Date.now() + MARKETS_SETTLE_MS)
        .filter((ms) => ms > 0);

      const wait = Math.min(MARKETS_MAX_WAIT_MS, ...(next.length > 0 ? next : [Number.MAX_VALUE]));
      timer = setTimeout(load, wait);
    };

    void load();
    reloadMarketsRef.current = () => {
      if (timer) clearTimeout(timer);
      void load();
    };
    return () => {
      alive = false;
      reloadMarketsRef.current = undefined;
      if (timer) clearTimeout(timer);
    };
  }, []);

  /**
   * The live connection. One per signed-in session, and none at all as a guest.
   *
   * This replaced a 5s poll. The poll's real problem was not its latency but
   * that it could only report what a *request* returned, so anything the server
   * did on its own — a resting order filling on an alarm — was invisible until
   * the user happened to ask again (M2/E6).
   *
   * The ticker and the header still share a clock, which is what M2/E9 was
   * about: one `tick` frame updates the marks, and the header re-derives from
   * those exact marks. There is no longer a way for the two to be reading
   * different prices, because there is only one price.
   */
  const streamRef = useRef<Stream | undefined>(undefined);

  /**
   * True while a recorded day is playing.
   *
   * A ref rather than state because `onTicks` is handed to the socket once, at
   * connect: reading a state value there would close over whatever it was when
   * the handler was created, and the socket would go on overwriting the
   * replayed ticker forever.
   */
  const replayingRef = useRef(false);

  const onTicks = useCallback((ticks: Tick[]) => {
    // The live feed does not get to overwrite a replay.
    //
    // Without this the ticker showed synthetic prices during a replay — and
    // showed symbols the recording does not even contain — because the socket
    // pushes a frame every second and simply landed last. Marks are left alone
    // deliberately: they value the account, which is still trading against the
    // live market, and freezing them would misprice a real position.
    if (replayingRef.current) {
      setMarks((previous) => {
        const next = new Map(previous);
        for (const t of ticks) next.set(t.symbol, t.last);
        return next;
      });
      return;
    }

    setQuotes((previous) => mergeTicks(previous, ticks));

    setMarks((previous) => {
      const next = new Map(previous);
      for (const t of ticks) next.set(t.symbol, t.last);
      return next;
    });
  }, []);

  /**
   * An account event: the fill, cancel or rejection that just happened.
   *
   * The snapshot replaces the base rather than being merged into it. It is the
   * server's whole answer, computed after the write — merging fields would let
   * a stale one survive a change that should have cleared it.
   */
  const onAccount = useCallback((accountId: string, next: AccountSnapshot, event: AccountEvent) => {
    // The hub deliberately sends frames for every account the socket owns. A
    // selected-account banner must ignore the other books, not let their next
    // fill overwrite the account the trader is inspecting.
    if (accountId !== accountIdRef.current) return;
    setOrdered(true);
    setSnapshot(next);
    setLines((l) => [...l, describeEvent(event)]);
  }, []);

  /**
   * What prices this trader just changed, and they did not ask for it.
   *
   * **Two things have to happen here and neither was happening.** The
   * scrollback gets a line: a trader whose key was revoked mid-session was told
   * nothing at all, and the first sign was prices quietly stopping. And the
   * header re-reads its markets, because the venue list on screen was derived
   * from an entitlement that has just gone away — its own schedule waits for
   * the next open/close transition, capped at fifteen minutes, which is the
   * right schedule for a venue changing state and the wrong one for the set of
   * venues changing underneath the reader.
   */
  const onFeed = useCallback(
    (change: FeedChange) => {
      reloadMarketsRef.current?.();
      // And the header re-reads WHOSE feed, which is the same argument one
      // level in: the badge was derived from a grant that has just changed, and
      // the frame carries `selected` and `because` but not `kind`, so the
      // honest way to get all three is to ask (`M4/E53`).
      void refreshFeed();
      setLines((l) => [...l, ...feedChangeLines(change)]);
    },
    [refreshFeed],
  );

  useEffect(() => {
    if (!session.userId) {
      streamRef.current?.close();
      streamRef.current = undefined;
      setLink("offline");
      return;
    }

    const stream = new Stream({
      onTicks,
      onAccount,
      onStatus: setLink,
      onWatching: applyWatched,
      onFeed,
    });
    streamRef.current = stream;
    stream.connect();
    // Whatever the shell is watching now, which after a `watch` and a sign-in is
    // not the default list.
    stream.watch([...watchedRef.current]);

    return () => {
      stream.close();
      streamRef.current = undefined;
    };
  }, [session.userId, onTicks, onAccount, applyWatched, onFeed]);

  /**
   * A guest still gets a ticker, and still gets it by polling.
   *
   * The socket is behind `requireUser`, so replacing the poll outright would
   * have left the signed-out landing screen with a dead ticker row — and a
   * market that visibly moves is most of what says this thing is alive. The
   * quotes endpoint is public, so this costs one request per 5s per guest and
   * opens no unauthenticated socket surface to rate-limit.
   *
   * Signed in, this does not run at all: the socket supersedes it.
   */
  useEffect(() => {
    if (session.userId) return;

    if (watched.length === 0) {
      setQuotes([]);
      return;
    }

    let alive = true;
    const poll = async () => {
      const quoted = await api.quotes([...watched]);
      if (alive && quoted.ok) {
        setQuotes((previous) => mergeTicks(previous, quoted.data.quotes));
      }
    };
    void poll();
    const timer = setInterval(poll, 5000);
    return () => {
      alive = false;
      clearInterval(timer);
    };
  }, [session.userId, watched]);

  /**
   * Replay playback: a stopwatch, and nothing else.
   *
   * Every frame is fetched with the elapsed time since play was pressed, and
   * every number in it — the price, the recorded instant, how far through — is
   * decided by the server from the recording. Nothing here advances a clock of
   * its own or interpolates between frames, which is what keeps the terminal
   * from computing something the API does not expose.
   *
   * It writes into the same ticker state the live feed uses, so a replayed
   * price renders exactly like a live one. That is deliberate: if a recorded
   * day needed its own rendering path, the ticker would have two ways to be
   * right and they would eventually disagree.
   */
  const replayRef = useRef<{ stop: () => void } | undefined>(undefined);

  const stopReplay = useCallback((): boolean => {
    if (!replayRef.current) return false;
    replayRef.current.stop();
    replayRef.current = undefined;
    replayingRef.current = false;
    // Hand the ticker back to the live feed immediately rather than waiting for
    // the next socket frame, so `replay stop` is visibly instant.
    setQuotes([]);
    return true;
  }, []);

  const startReplay = useCallback(
    (spec: { date: string; speed: number; symbols: string[] }) => {
      // A second `replay` supersedes the first rather than racing it into the
      // same ticker.
      stopReplay();

      const startedAt = Date.now();
      let alive = true;
      replayingRef.current = true;

      const tick = async () => {
        const frame = await api.replay(spec.date, Date.now() - startedAt, spec.speed, spec.symbols);
        if (!alive) return;

        if (!frame.ok) {
          setLines((l) => [...l, { kind: "err", text: `replay: ${frame.error}` }]);
          stopReplay();
          return;
        }

        // Replaced, not merged: a recorded day has its own symbol list, and
        // merging would strand live symbols on the ticker beside it.
        setQuotes((previous) => replaceTicks(previous, frame.data.quotes));

        if (frame.data.finished) {
          setLines((l) => [
            ...l,
            { kind: "note", text: `replay of ${frame.data.date} finished — ticker is live again` },
          ]);
          stopReplay();
        }
      };

      void tick();
      const timer = setInterval(tick, 1000);
      replayRef.current = {
        stop: () => {
          alive = false;
          clearInterval(timer);
        },
      };
    },
    [stopReplay],
  );

  // A replay must not outlive the component that started it. The return value
  // is discarded deliberately: a cleanup function may not return one.
  useEffect(
    () => () => {
      stopReplay();
    },
    [stopReplay],
  );

  /**
   * Equity, marked to the latest prices the ticker is showing.
   *
   * Derived rather than stored, so it cannot go stale: any change to either the
   * snapshot or the marks recomputes it. See `marking.ts` for why computing it
   * here does not break the rule that the terminal shows only what the API
   * exposes.
   */
  /**
   * Watch every symbol the selected account holds (`M8/E25`).
   *
   * The header re-prices a holding only from ticks, and ticks arrive only for
   * watched symbols, so a holding outside the ticker kept the server's last
   * figure and equity stood still while the market moved — first seen when a
   * sync from the live account brought in symbols the default ticker does not
   * carry. Symbols added here are dropped again once nothing holds them.
   */
  useEffect(() => {
    const held = new Set((snapshot?.positions ?? []).map((p) => p.symbol.toUpperCase()));
    const watching = new Set(watchedRef.current.map((x) => x.toUpperCase()));
    const missing = [...held].filter((symbol) => !watching.has(symbol));
    const released = [...heldWatchRef.current].filter((symbol) => !held.has(symbol));
    if (released.length > 0) {
      for (const r of released) heldWatchRef.current.delete(r);
      applyWatched(watchedRef.current.filter((x) => !released.includes(x.toUpperCase())));
      streamRef.current?.unwatch(released);
    }
    if (missing.length > 0) {
      applyWatched([...watchedRef.current, ...missing]);
      streamRef.current?.watch(missing);
      for (const m of missing) heldWatchRef.current.add(m);
    }
  }, [snapshot, applyWatched]);

  const marked = useMemo(
    () => (snapshot ? markPortfolio(snapshot, marks) : undefined),
    [snapshot, marks],
  );

  /**
   * Where the tour would say you are, from facts the shell already holds.
   *
   * Derived rather than tracked, so it is right after a reload, after signing
   * out, and for somebody who typed the commands directly instead of following
   * along — which is what a curious person does first.
   */
  const onboarding = useMemo<OnboardingState>(
    () => ({
      signedIn: Boolean(session.userId),
      // Not signed in yet means nothing is outstanding: the tour's first step is
      // the sign-in that will raise the question.
      agreed: !session.agreementRequired,
      hasAccount: snapshot !== undefined,
      pricedSomething: priced,
      choseFeed: feedChosen,
      hasOrdered: ordered || (snapshot?.open_orders ?? 0) > 0,
      hasPosition: (snapshot?.positions.length ?? 0) > 0,
    }),
    [session.userId, session.agreementRequired, snapshot, priced, ordered, feedChosen],
  );

  /**
   * The rows, with the two things a setup would otherwise each work out for
   * itself: whether this is a price you could trade against right now, and how
   * far the day has moved.
   *
   * Both derivations live in `ticker.ts`, on scaled integers. Nothing here is
   * on the money path, but two ways of reading a price in one codebase is how
   * the money path acquires a float later — and with two setups there would
   * otherwise be two copies of each.
   */
  const quotes = useMemo<Quote[]>(
    () => rawQuotes.map((q) => ({ ...q, open: isOpen(q), changePercent: changePercent(q) })),
    [rawQuotes],
  );

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, []);

  /**
   * Wear a different look, and remember it.
   *
   * Returns whether the id was known, so the command can say so rather than
   * silently doing nothing. The preference is `localStorage` and not the
   * server: there is no user-preferences surface yet, and pretending otherwise
   * would mean inventing one here. Explicitly carried forward in the iteration.
   */
  const chooseSetup = useCallback((id: string) => {
    if (!knownSetup(id)) return false;
    setSetupId(id);
    remember(SETUP_KEY, id);
    return true;
  }, []);

  const chooseTheme = useCallback((id: string) => {
    if (!knownTheme(id)) return false;
    setThemeChoice(id);
    remember(THEME_KEY, id);
    return true;
  }, []);

  /**
   * Lines entered while a command is running (M7/E9).
   *
   * The prompt stays enabled while busy so a trader can type ahead (M2/E1), and
   * Enter used to return early: no echo, no queue, no hint, and the text stayed
   * in the box to run into whatever was typed next. Now the line is cleared,
   * marked as queued, and run in order once nothing is in flight.
   */
  const queued = useRef<string[]>([]);
  /**
   * The running command's Escape (M9/E6): a command that waits minutes —
   * `rebalance tune` — stops waiting when it is aborted. The work it started
   * goes on; only following it stops.
   */
  const running = useRef<AbortController | undefined>(undefined);
  const liveKey = useRef(0);
  const submitRef = useRef<(raw: string) => Promise<void>>(async () => {});

  /**
   * A question empties the queue. The next line typed is its answer — a code,
   * a key — and a typed-ahead `positions` must never be sent as somebody's
   * one-time code.
   */
  const dropQueued = useCallback(() => {
    const n = queued.current.length;
    if (n === 0) return;
    queued.current = [];
    setLines((l) => [
      ...l,
      {
        kind: "note",
        text: `dropped ${n} queued command${n === 1 ? "" : "s"} — answer the question first`,
      },
    ]);
  }, []);

  const submit = useCallback(
    async (raw: string) => {
      const command = raw.trim();
      if (!command) return;
      if (busy) {
        queued.current.push(command);
        setInput("");
        setLines((l) => [...l, { kind: "note", text: `queued: ${command}` }]);
        return;
      }

      // Answering a question, not running a command.
      //
      // Deliberately BEFORE the history push. An email is an ordinary thing to
      // find in ArrowUp; a one-time code sitting there for the rest of the
      // session is a small leak with no upside, and the two arrive through the
      // same door. Neither is echoed as a command either — the scrollback shows
      // the question and the outcome, which is the record worth keeping.
      if (pending) {
        const answer = pending;
        setPending(undefined);
        setInput("");
        setBusy(true);
        try {
          const result = await answer.onAnswer(command);
          setLines((l) => [...l, ...result.lines]);
          if (result.prompt) {
            setPending(result.prompt);
            dropQueued();
          }
        } catch (e) {
          setLines((l) => [
            ...l,
            { kind: "err", text: e instanceof Error ? e.message : "that did not work" },
          ]);
        } finally {
          setBusy(false);
          requestAnimationFrame(() => {
            scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
            inputRef.current?.focus();
          });
        }
        return;
      }

      writeHistory(pushHistory(historyRef.current.list, command));
      setInput("");

      if (isClear(command)) {
        setLines([]);
        return;
      }

      setLines((l) => [...l, { kind: "echo", text: command }]);

      // The guided run's third step is "ask for a price yourself", and these are
      // the three commands that do it. Read from the line rather than inferred
      // from state, because `watch` on a symbol already in the default five is
      // a deliberate act that changes nothing observable.
      const verb = command.split(/\s+/)[0]?.toLowerCase();
      if (verb === "watch" || verb === "unwatch" || verb === "quote") setPriced(true);

      setBusy(true);
      const controller = new AbortController();
      running.current = controller;
      const key = `live-${++liveKey.current}`;
      try {
        const result = await runCommand(command, {
          session,
          setSession,
          refresh: () => void refresh(),
          /**
           * Re-read the feed choice rather than assume the command made one.
           *
           * `feed alpaca` can be refused — a trader is not entitled to a feed
           * they hold no key for — and an optimistic flag here would tick the
           * tour's step for a choice the server rejected. Every other predicate
           * in this file is derived from what the server says; this is the same
           * rule reaching one command further.
           */
          refreshFeed: () => void refreshFeed(),
          // Through the shell, not straight at the socket: signed out there is
          // no socket, and `watch`/`unwatch` used to report success having
          // changed nothing at all.
          watch: watchSymbols,
          unwatch: unwatchSymbols,
          watching: () => [...watchedRef.current],
          disconnect: () => streamRef.current?.close(),
          // The catalogue is injected rather than imported, for the reason the
          // socket is: it keeps the grammar testable without React, and it is
          // what stops `commands.ts` becoming a second place a setup is named.
          setups: () =>
            SETUPS.map((s) => ({ id: s.id, title: s.title, current: s.id === setup.id })),
          setSetup: chooseSetup,
          themes: () =>
            THEMES.map((t) => ({ id: t.id, title: t.title, current: t.id === themeId })),
          setTheme: chooseTheme,
          startReplay,
          stopReplay,
          // The checklist and the step, both only when asked. There is no
          // `setTour` any more: the tour has nothing to switch off now that it
          // never speaks unbidden, and a stored preference for a thing that
          // does not happen is a setting nobody can observe the effect of.
          tour: () => ({
            lines: [...checklist(onboarding), ...say(onboarding)],
            finished: currentStep(onboarding) === undefined,
          }),
          // One line this command rewrites in place, rather than a line per poll.
          progress: (text) =>
            setLines((l) => {
              const line: Line = { kind: "note", text, pre: true, live: key };
              const at = l.findIndex((x) => x.live === key);
              return at === -1 ? [...l, line] : [...l.slice(0, at), line, ...l.slice(at + 1)];
            }),
          signal: controller.signal,
          history: () => historyRef.current.list,
          clearHistory: () => writeHistory([]),
        });
        setLines((l) => [...l, ...result.lines]);
        if (result.prompt) {
          setPending(result.prompt);
          dropQueued();
        }
      } catch (e) {
        setLines((l) => [
          ...l,
          { kind: "err", text: e instanceof Error ? e.message : "command failed" },
        ]);
      } finally {
        if (running.current === controller) running.current = undefined;
        setBusy(false);
        requestAnimationFrame(() => {
          scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
          // Belt and braces for M2/E1: the prompt should never have lost focus
          // (it is no longer disabled while busy), but if anything else takes
          // it, a terminal that needs a mouse click to accept the next command
          // is not a terminal.
          inputRef.current?.focus();
        });
      }
    },
    [
      busy,
      pending,
      refresh,
      refreshFeed,
      session,
      setSession,
      setup.id,
      themeId,
      chooseSetup,
      chooseTheme,
      startReplay,
      stopReplay,
      watchSymbols,
      unwatchSymbols,
      onboarding,
      dropQueued,
      writeHistory,
    ],
  );
  submitRef.current = submit;

  // Run what was typed ahead, one line at a time, once nothing is in flight and
  // nothing is being asked.
  useEffect(() => {
    if (busy || pending) return;
    const next = queued.current.shift();
    if (next !== undefined) void submitRef.current(next);
  }, [busy, pending]);

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      void submit(input);
      return;
    }
    // Shell-style history.
    if (e.key === "ArrowUp") {
      e.preventDefault();
      const next = Math.min(historyAt + 1, history.length - 1);
      if (next >= 0 && history[next]) {
        setHistoryAt(next);
        setInput(history[next]);
      }
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      const next = historyAt - 1;
      setHistoryAt(next);
      setInput(next >= 0 ? (history[next] ?? "") : "");
      return;
    }
    // Escape gets you out of a question. Without it the only way to abandon a
    // half-finished sign-in is to reload the page, because every line typed is
    // being read as the answer.
    // Escape while a command waits stops the waiting (M9/E6). A command that
    // never waits ignores it, so nothing else changes.
    if (e.key === "Escape" && busy && !pending && running.current) {
      e.preventDefault();
      running.current.abort();
      return;
    }
    if (e.key === "Escape" && pending) {
      e.preventDefault();
      setPending(undefined);
      setInput("");
      setLines((l) => [...l, { kind: "note", text: "cancelled" }]);
      return;
    }
    if (e.key === "Tab") {
      e.preventDefault();
      // The catalogue is assembled here rather than reached for inside
      // `complete.ts`, for the reason every other seam in this file exists: the
      // grammar and the completion stay testable without a DOM, and setup and
      // theme ids keep arriving from the one place that names them.
      const result = complete(input, {
        commands: COMMAND_NAMES,
        // Setup and theme ids and the watch list are closed sets the shell
        // already holds, so what Tab offers never needs the server.
        options: (command, typed, partial) =>
          optionsForCommand(command, typed, partial, {
            setups: SETUPS.map((x) => x.id),
            themes: THEMES.map((x) => x.id),
            watched: watchedRef.current,
            commands: COMMAND_NAMES,
            feeds: feedNamesRef.current,
          }),
      });
      if (result.input !== input) setInput(result.input);
      if (result.lines) setLines((l) => [...l, ...(result.lines as Line[])]);
      return;
    }
  };

  return {
    lines,
    input,
    busy,
    session,
    quotes,
    snapshot,
    marked,
    link,
    pending,
    markets,
    feed,
    liveVenue,
    setupId: setup.id,
    themeId,
    setInput,
    submit: (raw: string) => void submit(raw),
    onKeyDown,
    scrollRef,
    inputRef,
    shellRef,
  };
}

/** The look worn when nothing has been chosen — named so a test can assert it. */
export const FALLBACK_SETUP = DEFAULT_SETUP;

/**
 * Turn a pushed account event into a scrollback line.
 *
 * **This is what M2/E6 and M2/E7 asked for.** Both events it exists for happen
 * on a `SymbolBookDO` alarm rather than in response to a request, so there was
 * no response for the terminal to read an outcome from — a stop filled by itself
 * and the screen said nothing, and a stop rejected at trigger time simply
 * stopped appearing in `orders open` with no explanation offered anywhere.
 *
 * The id is truncated to eight characters, matching `orders`, so a line here and
 * a row there are recognisably the same order.
 */
export function describeEvent(event: AccountEvent): Line {
  const id = event.order_id.slice(0, 8);
  const side = `${event.side} ${event.qty} ${event.symbol}`;

  switch (event.kind) {
    case "synced":
      // Pushed to every open terminal, including one that did not type `sync` —
      // a scheduled sync has nobody typing at all.
      return {
        kind: "note",
        text: `· sync ${id} reset this account to its connected Alpaca account — \`sync show ${id}\``,
      };
    case "filled":
      return {
        kind: "out",
        text: `✓ ${id} ${side} filled ${event.filled_qty ?? event.qty} @ ${event.fill_price ?? "?"}`,
      };
    case "rejected":
      // An error line, deliberately. A resting order that was accepted and then
      // refused is the outcome a trader most needs to notice, and M2/E7 was
      // raised because it was the one that had no line at all.
      return {
        kind: "err",
        text: `✗ ${id} ${side} rejected at trigger — ${event.reason ?? "no reason given"}`,
      };
    case "expired":
      return { kind: "note", text: `· ${id} ${side} expired` };
    case "canceled":
      return { kind: "note", text: `· ${id} ${side} canceled` };
    case "stop_fired":
      return { kind: "note", text: `· ${id} ${side} stop triggered — working` };
    case "replaced":
      return { kind: "note", text: `· ${id} ${side} replaced` };
    default:
      return { kind: "note", text: `· ${id} ${side} ${event.status}` };
  }
}
